// ═══════════════════════════════════════════════════════════════════════════
// cashTargetBasis.js — a working cash target is a CASH promise. The futures
// number keyed into the elevator's system is a DERIVED, PERISHABLE artifact,
// and this file re-derives it against the current basis sheet.
//
// ── THE DEFECT THIS EXISTS FOR (Kevin, 2026-08-24, measured) ────────────────
//
// A grower says "sell 5,000 bu at $5.25 cash". Basis that day is +0.15, so the
// working order goes in at $5.10 futures. Basis then moves and NOTHING
// re-derives the order. Woodlawn Farms, ticket dated 2026-03-09, LeRoy,
// December delivery, Z26:
//
//   entered   2026-03-09  basis +0.15  ->  $5.10 futures  =  $5.25 cash
//   current   2026-07-10  basis -0.01  ->  $5.26 futures  =  $5.25 cash
//
// The order sat at $5.10. CZ26 settled $5.085 on 2026-08-21. A fill at $5.10
// pays $5.09 cash against $5.25 promised. The board did nothing wrong; the
// basis moved 16c and the order did not.
//
// Brian Thompson Farms, 2026-03-12, BATAVIA, Oct-Nov, Z26, 10,000 bu: entry
// basis +0.15 (matches the 2026-03-09 sheet exactly), current -0.13. That is
// 28c and $2,800, and it was still working with nothing pencilled on it.
//
// ── THE IDENTITY THAT MAKES THIS SIMPLE ────────────────────────────────────
//
// Given the ticket's own arithmetic holds (cash = futures + basis, which is
// PRINTED on the green ticket and enforced as a CHECK constraint in sql/048):
//
//     cash shortfall  ==  required adjustment  ==  -(basis drift)
//
// The amount the futures order must move is EXACTLY the amount the basis
// moved, and the cash the grower loses if it fills unchanged is the same
// number again. There is no third quantity to compute and no place for a
// rounding fudge to hide. check-cash-targets asserts the identity directly.
//
// ── WHAT THIS FILE DELIBERATELY DOES NOT DO ────────────────────────────────
//
// NO FUTURES. Kevin, 2026-08-24: "I dont need you to track futures, I need you
// to track basis changes." Nothing here reads a board price, so nothing here
// can go stale when the futures feed does. Ranking by how close the board sits
// to a target was designed and then CUT: at 10-50 working orders every stale
// target is equally actionable, because the fix is identical (go change the
// order), and proximity only ever spoke to urgency.
//
// NO FILL DETECTION. Our feed is settle-only and cannot confirm an intraday
// touch — the reason grain target alerts are paused (GRAIN_TARGET_ALERTS_ENABLED
// = false). `status` is set by a human. This file never infers a fill.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

const { BASIS_MARKET_ALIASES, slugForMarket, slugsForTown } = require("./basisMarketAliases");

// ── money in hundredths of a cent ──────────────────────────────────────────
// Every comparison and every subtraction happens on INTEGERS. Basis is quoted
// in cents (+0.15), cash in cents ($5.25), futures in quarter-cents ($5.0850);
// x10000 holds all three exactly. Deriving a target with binary floats and
// then comparing with a tolerance is how a $5.09 becomes a $5.10 — the same
// trap _home.js's assertNumeralsContained documents from the other direction.
const SCALE = 10000;
const q = (x) => Math.round(Number(x) * SCALE);
const unq = (n) => n / SCALE;

// ── a TYPED basis, in cents ───────────────────────────────────────────────
// Used for a target on a basis contract, where the basis is fixed by the
// contract and the board does not apply.
//
// ⚠ NO UNIT GUESSING. The first cut read `Math.abs(n) >= 3 ? n / 100 : n`, so
// +15 and -6 came out right while +2 became $2.00 — turning a $5.48 futures
// target into $3.50 on an entry that looked perfectly valid. A number that is
// syntactically fine and silently ~$2/bu wrong is the worst shape a parse can
// have. Found by an independent audit, 2026-08-24.
//
// WHOLE CENTS ONLY, and that is measured rather than assumed: 0 of 5,921 basis
// values across grain_basis_board and basis_sheet_observations are anything
// but a whole cent (the range is -$2.50 to +$1.19). So a decimal point is
// genuinely ambiguous — "0.15" reads as fifteen cents to a human and as
// fifteen hundredths of a cent to the rule — and it is REFUSED rather than
// resolved by picking one. The caller shows the reason; the field says cents.
const MAX_BASIS_CENTS = 500;   // sanity bound, not a rule: refuses a fat-fingered 1500
function parseBasisCents(input) {
  const raw = String(input == null ? "" : input).trim().replace(/[¢c]/gi, "");
  if (!raw) return null;
  if (!/^[+-]?\d+$/.test(raw)) return null;      // a decimal point is ambiguous -> refuse
  const cents = Number(raw);
  if (!Number.isFinite(cents)) return null;
  if (Math.abs(cents) > MAX_BASIS_CENTS) return null;
  return Math.round(cents) / 100;
}
// ── delivery window ────────────────────────────────────────────────────────
// The ticket writes "Oct-Nov 26" or "December"; the sheet writes "Oct-Nov" or
// "26-Dec". Both normalize to a MONTH LIST with the year stripped, because the
// year is already carried — exactly and separately — by the futures contract
// (Z26). Matching on (window, contract) is therefore unique without the window
// having to agree about how it spells the year.
const MONTH3 = {
  jan: "JAN", january: "JAN", feb: "FEB", february: "FEB", mar: "MAR", march: "MAR",
  apr: "APR", april: "APR", may: "MAY", jun: "JUN", june: "JUN", jul: "JUL", july: "JUL",
  aug: "AUG", august: "AUG", sep: "SEP", sept: "SEP", september: "SEP",
  oct: "OCT", october: "OCT", nov: "NOV", november: "NOV", dec: "DEC", december: "DEC",
};
const NON_MONTH_WINDOW = { harv: "HARVEST", harvest: "HARVEST" };

function windowTokens(raw) {
  return String(raw || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

// null means REFUSED, never a guess. A window this cannot read (a new label, a
// typo, "NEW CROP") stops the target from being evaluated and gets reported —
// the alternative is silently pricing December off the Oct-Nov row, which is
// the 5c error found on Woodlawn's own ticket on 2026-08-24.
function normalizeDeliveryWindow(raw) {
  const out = [];
  for (const tok of windowTokens(raw)) {
    if (/^\d{2}$|^\d{4}$/.test(tok)) continue;             // a crop year, not a month
    const m = MONTH3[tok] || NON_MONTH_WINDOW[tok];
    if (!m) return null;                                    // unknown token -> refuse
    if (!out.includes(m)) out.push(m);
  }
  return out.length ? out.join("-") : null;
}

// ── delivery point -> the sheet columns that carry it ──────────────────────
// A TICKET names ONE delivery point ("LeRoy"). A SHEET COLUMN names a SET of
// them ("Dlv Leroy & TDAg"), and which column carries a given point CHANGES
// FROM SHEET TO SHEET. Measured 2026-08-24: LeRoy's bid lives under
// howlett_leroy on the 2026-06-09 sheet and under howlett_leroy_tdag on
// 2026-07-10; Batavia flips the other way. Key on equality and "the latest
// LeRoy basis" returns -0.06 or +0.09 depending on which slug you happened to
// pick — a 15c error in the exact number this file exists to compute.
//
// So the lookup is MEMBERSHIP, not equality: every column whose printed header
// contains the point as a whole word is a candidate. Matching is on tokens and
// requires ALL of the point's tokens, so "Del Mar" does not match "Del Mar"
// alone by accident of substring. It is never fuzzy — that is the doctrine
// basisMarketAliases.js sets out and the reason slugForMarket returns null for
// an unknown header rather than picking the closest one.
function deliveryPointCandidates(point) {
  const want = windowTokens(point);
  const byslug = new Map();
  if (!want.length) return { point, slugs: [], labels: {} };

  // A TOWN FIRST. The ticket says "Lakeville"; the sheet says "Avon & Lkville".
  // Those are the same bid, and no amount of token matching gets there from a
  // printed contraction — which is why DELIVERY_TOWN_SLUGS is written down
  // rather than derived.
  const townSlugs = slugsForTown(point);
  if (townSlugs) {
    const labels = {};
    for (const [printed, slug] of Object.entries(BASIS_MARKET_ALIASES)) {
      if (townSlugs.includes(slug)) (labels[slug] = labels[slug] || []).push(printed);
    }
    return { point, slugs: townSlugs, labels, viaTown: true };
  }

  for (const [printed, slug] of Object.entries(BASIS_MARKET_ALIASES)) {
    const toks = new Set(windowTokens(printed));
    if (!want.every((w) => toks.has(w))) continue;
    if (!byslug.has(slug)) byslug.set(slug, []);
    byslug.get(slug).push(printed);
  }
  return { point, slugs: [...byslug.keys()], labels: Object.fromEntries(byslug) };
}

// ── grain_basis_board -> observations ──────────────────────────────────────
// THE ADAPTER EXISTS BECAUSE THE FIRST CUT READ THE WRONG TABLE (2026-08-24).
// `basis_sheet_observations` is the photo-import ARCHIVE and stopped at
// 2026-07-10; `grain_basis_board` is the LIVE board — 183 rows, sheet version
// 8-B, and what Grain Marketing actually renders. Priced off the archive this
// engine was 45 days stale and every number it produced looked reasonable:
// Avon July-2027 wheat read -0.35 where the board says -0.55, so it
// "corrected" Kevin against his own sheet. A tool built to catch stale basis
// was running on stale basis, and nothing in the output said so.
//
// So the caller converts the board here rather than hand-shaping rows, and the
// shape it produces is the only thing resolveCurrentBasis is ever given.
//
// `location` is the PRINTED column header, so it goes through slugForMarket()
// exactly like the archive does — and an unmapped header REFUSES rather than
// being slugged generically, which is the whole doctrine of that file. A board
// column nobody has mapped is a question for a human; guessing it would file
// one elevator's basis under another.
// ── THE SHEET STAMP IS THE ELEVATOR'S OWN PRINTING, NOT AN ISO DATE ────────
//
// `grain_basis_board.sheet_updated` is whatever the sheet prints at the top of
// itself, and the format CHANGED under us. Boards 8-C and 8-D stamped an
// ISO-leading string, so the two callers that needed a DATE out of it took the
// first ten characters and were right by luck. Board 9-C stamps
//
//     "9/3/2026 13:56"
//
// and the first ten characters of that are "9/3/2026 1", which Postgres
// refuses: 22007 invalid input syntax for type date. Measured 2026-09-05,
// that is the value on all 238 live board rows.
//
// It broke BOTH paths that write `entry_basis_sheet_date` — the ✓ that
// re-anchors a target to today's board, and the form that creates one — so
// the button did nothing and the page showed a raw PostgREST body. Every
// target entered before the format changed carries a correct date (2026-08-21,
// 2026-08-24), which is why nothing looked wrong until the board rolled.
//
// ⚠ AN UNREADABLE STAMP RETURNS NULL AND NEVER A SUBSTITUTE. The obvious
// fallback — `updated_at` — is when WE FETCHED the sheet, not when the
// elevator published it, and this column is the sheet's own date. A target
// stamped with our fetch time cannot be checked against the paper later, which
// is the entire reason the column exists. Nullable, so a blank is a real
// answer.
function sheetStampToDate(stamp) {
  const t = String(stamp == null ? "" : stamp).trim();
  if (!t) return null;
  // already ISO (date or datetime): take the date part
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/);
  if (iso) return validDate(+iso[1], +iso[2], +iso[3]);
  // US M/D/YYYY, with or without a trailing time
  const us = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ ,]|$)/);
  if (us) return validDate(+us[3], +us[1], +us[2]);
  return null;
}
function validDate(y, m, d) {
  if (!(y >= 1900 && y <= 2200) || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  // rejects 2/30 and friends rather than letting JS roll them forward into a
  // date the sheet does not carry
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function boardToObservations(boardRows, { asOf } = {}) {
  const out = [];
  const unmapped = new Set();
  for (const r of boardRows || []) {
    const source = slugForMarket(r.location);
    if (!source) { unmapped.add(r.location); continue; }
    out.push({
      source,
      // The board carries ONE current sheet, so every row shares its date. Use
      // the sheet's own stamp where the caller has it, never today's date —
      // a target report that dates the basis to "now" cannot be checked later.
      sheet_date: asOf || r.sheet_updated || r.updated_at || "",
      commodity: String(r.commodity || "").trim().toLowerCase(),
      delivery_window: r.ship_month,
      futures_contract: r.cme_contract,
      basis: r.basis,
      bid_structure: r.term,
      no_bid: r.basis === null || r.basis === undefined,
      sheet_version: r.sheet_version || null,
    });
  }
  return { observations: out, unmappedLocations: [...unmapped] };
}

// ── the current basis for one ticket's cell ────────────────────────────────
// "Current" is THE POINT'S OWN LATEST SHEET, not the newest sheet in the
// table and not a date window. Kevin, 2026-07: "the basis stays active until i
// upload a new one" — a month-old sheet IS the current basis, and a staleness
// cliff silently stops the whole thing working. Equally, a cell is never
// carried forward off a SUPERSEDED sheet: if the point published a sheet that
// does not print this window, the honest answer is that the window is not on
// the current sheet (it may have rolled off), not last month's number.
function resolveCurrentBasis({ deliveryPoint, commodity, deliveryWindow, futuresContract, bidStructure, observations }) {
  const cand = deliveryPointCandidates(deliveryPoint);
  if (!cand.slugs.length) return { basis: null, reason: "unknown_delivery_point", candidates: cand };

  const want = normalizeDeliveryWindow(deliveryWindow);
  if (!want) return { basis: null, reason: "unreadable_window", candidates: cand };

  const contract = String(futuresContract || "").trim().toUpperCase();
  if (!contract) return { basis: null, reason: "no_futures_contract", candidates: cand };

  const slugs = new Set(cand.slugs);
  const crop = String(commodity || "").trim().toLowerCase();
  const rows = (observations || []).filter(
    (r) => slugs.has(r.source) && String(r.commodity || "").trim().toLowerCase() === crop
  );
  if (!rows.length) return { basis: null, reason: "no_observations_for_point", candidates: cand };

  // LATEST SHEET WINS, AND THE COMPARISON MUST NOT BE TWO FORMATS OF STRING.
  // Every row in one board pull shares a stamp, so this was never wrong in
  // production — but "9/3/2026 13:56" sorts above "2026-08-24" for a reason
  // that has nothing to do with dates, and the moment two formats meet here
  // the wrong sheet wins silently. Rank on the parsed date where the stamp
  // parses; fall back to the raw string only when it does not, so an
  // unreadable stamp still picks deterministically instead of throwing.
  const rank = (r) => sheetStampToDate(r.sheet_date) || String(r.sheet_date || "");
  const currentSheetDate = rows.reduce((a, r) => (rank(r) > a ? rank(r) : a), "");
  const onSheet = rows.filter((r) => rank(r) === currentSheetDate);

  const windowMatches = onSheet.filter(
    (r) =>
      normalizeDeliveryWindow(r.delivery_window) === want &&
      String(r.futures_contract || "").trim().toUpperCase() === contract
  );

  if (!windowMatches.length) {
    const available = [...new Set(
      onSheet.map((r) => `${r.delivery_window} / ${r.futures_contract}`)
    )].sort();
    return { basis: null, reason: "window_not_on_current_sheet", currentSheetDate, available, candidates: cand };
  }

  // DELIVERED AND PICKED-UP ARE DIFFERENT BIDS, and the delivery POINT does not
  // always separate them. Measured 2026-08-24: 406 pickup rows sit on
  // non-pickup slugs, and howlett_general_unspecified carries delivered AND
  // pickup rows under ONE source — so a point-only lookup there would see two
  // legitimate numbers and (correctly but uselessly) refuse as a conflict.
  // Narrowing is a REQUIREMENT, never a preference: a structure the current
  // sheet does not print for this window is reported, not answered with the
  // other structure's number, which differs by the whole haul.
  const wantStruct = String(bidStructure || "").trim().toLowerCase() || null;
  const matches = wantStruct
    ? windowMatches.filter((r) => String(r.bid_structure || "").trim().toLowerCase() === wantStruct)
    : windowMatches;

  if (!matches.length) {
    return {
      basis: null,
      reason: "bid_structure_not_on_current_sheet",
      currentSheetDate,
      wanted: wantStruct,
      available: [...new Set(windowMatches.map((r) => r.bid_structure))].sort(),
      candidates: cand,
    };
  }

  // NO_BID IS NOT ZERO. The elevator declining to bid a window is a refusal,
  // and recording it as 0.00 would read as "bid flat" — the opposite. Captured
  // on live import since 2026-08-20 precisely so the two stay distinguishable.
  const bids = matches.filter((r) => !r.no_bid && r.basis !== null && r.basis !== undefined);
  if (!bids.length) {
    return { basis: null, reason: "no_bid", currentSheetDate, candidates: cand };
  }

  // Two columns carrying this point, on the SAME sheet, at DIFFERENT numbers is
  // two genuinely different bids, not a spelling drift — refuse and show both.
  // It does not happen today (the LeRoy pair never co-occurs on one sheet,
  // which is the evidence they are one column relabelled), so this guard costs
  // nothing until the sheet layout changes under us.
  const distinct = [...new Set(bids.map((r) => q(r.basis)))];
  if (distinct.length > 1) {
    return {
      basis: null,
      reason: "conflicting_bids",
      currentSheetDate,
      conflict: bids.map((r) => ({ source: r.source, basis: Number(r.basis) })),
      candidates: cand,
    };
  }

  return {
    basis: unq(distinct[0]),
    reason: null,
    currentSheetDate,
    sources: [...new Set(bids.map((r) => r.source))],
    normalizedWindow: want,
    candidates: cand,
  };
}

// ── one target, re-derived ─────────────────────────────────────────────────
const LANES = ["burn", "miss", "unchanged", "basis_locked", "unresolved", "ticket_error",
                      "unsupported", "closed"];

// ⚠ THIS FUNCTION IS HANDED ROWS IN TWO SHAPES AND MUST READ BOTH.
// The form builds camelCase; PostgREST returns snake_case AND renders every
// numeric as a STRING ("5.3", "5000", "-0.23"). The first cut wrote
//   cashTarget: target.cashTarget == null ? null : Number(target.cashTarget ?? target.cash_target)
// where the guard fires on the camelCase key being undefined and the
// `?? cash_target` fallback is DEAD CODE — so every SAVED target read as
// having no cash price, fell to lane `ticket_error`, and printed a sheet with
// the basis and the cash both BLANK. Kevin found it by printing one.
//
// Nothing in check-cash-targets caught it because every fixture was camelCase:
// the checker tested the shape the tests used, not the shape the database
// returns. There is now a snake-case-and-strings round trip beside them.
const pick = (a, b) => (a === undefined || a === null ? b : a);
const numOrNull = (v) => (v === undefined || v === null || v === "" ? null : Number(v));

function evaluateCashTarget(target, observations) {
  const base = {
    id: target.id ?? null,
    farmerName: pick(target.farmerName, target.farmer_name) ?? null,
    commodity: target.commodity ?? null,
    bushels: numOrNull(pick(target.bushels, target.bushels)),
    deliveryPoint: pick(target.deliveryPoint, target.delivery_point) ?? null,
    deliveryWindow: pick(target.deliveryWindow, target.delivery_window) ?? null,
    futuresContract: pick(target.futuresContract, target.futures_contract) ?? null,
    bidStructure: pick(target.bidStructure, target.bid_structure) ?? "delivered",
    basisLocked: pick(target.basisLocked, target.basis_locked) ?? false,
    cashTarget: numOrNull(pick(target.cashTarget, target.cash_target)),
    basisAtEntry: numOrNull(pick(target.basisAtEntry, target.basis_at_entry)),
    futuresTargetEntered: numOrNull(pick(target.futuresTargetEntered, target.futures_target_entered)),
    orderType: pick(target.orderType, target.order_type) ?? "cash_target",
    status: target.status ?? "working",
    enteredOn: pick(target.enteredOn, target.entered_on) ?? null,
    // ⚠ THE OTHER TWO DATES THE RESTING WINDOW IS BUILT FROM, AND THEY WERE
    // BOTH BEING DROPPED HERE.
    //
    // restingSince() takes the LATEST of entered_on, updated_at and
    // fill_dismissed_at — a re-anchor moves the level so settles before it
    // belong to the old order, and a dismissal means a human has already looked
    // at everything up to that moment. This row is what the Targets page hands
    // to boardClosedThrough() and targetApproach(), and carrying only enteredOn
    // meant BOTH of them measured from the entry date forever.
    //
    // Two live consequences, found 2026-10-06 while rendering the new approach
    // line and seeing it disagree with the same rule run over the raw row:
    //
    //   · DISMISSING A FILL NOTICE DID NOTHING. dismissFill writes
    //     fill_dismissed_at and its own comment says "only settles AFTER this
    //     moment count from now on" — correct, and unreachable, so the row came
    //     straight back on the next load.
    //   · A RE-ANCHORED ORDER WAS JUDGED ON THE OLD LEVEL'S SETTLES. Cripple
    //     Creek's WN27 was re-anchored 2026-09-05; the window still opened on
    //     08-27, so the 09-01 settle of $7.9475 — which happened while the order
    //     rested somewhere else — counted toward it. Measured: closest approach
    //     45c short from the entry date, 76c short from the re-anchor.
    //
    // The comment on dismissFill described behaviour the code could not deliver,
    // which is the class CLAUDE.md already records ("the comment above the
    // function already said 'passes through this filter' while the code filtered
    // it"). Carried through as the strings they are stored as; restingSince does
    // its own slicing.
    updatedAt: pick(target.updatedAt, target.updated_at) ?? null,
    fillDismissedAt: pick(target.fillDismissedAt, target.fill_dismissed_at) ?? null,
    buyer: pick(target.buyer, target.buyer) ?? null,
    notes: target.notes ?? null,
  };

  if (base.status !== "working") return { ...base, lane: "closed", reason: `status is ${base.status}` };

  // ── A CASH TARGET ON A LOCKED BASIS CONTRACT IS IMMUNE, AND SAYING
  // OTHERWISE IS THE EXPENSIVE ERROR (Kevin, 2026-08-24: "those would be cash
  // targets on a locked basis contract. So if basis moves on those, they are
  // not affected, they locked the basis").
  //
  // The grower has already signed a basis contract, so the basis in the cash
  // = futures + basis identity is FIXED BY CONTRACT and no longer tracks the
  // board. The futures order is correct forever and must not be touched.
  //
  // This is not a filter for tidiness — it is a FALSE POSITIVE that costs
  // money. John B Martin's 10,000 bu ticket would otherwise have been reported
  // as 28c stale and "corrected" by $2,800 in the grower's disfavour, off a
  // basis he does not have. A target that must not move is REPORTED as immune
  // rather than dropped, so nobody helpfully updates it by hand either.
  if (base.basisLocked) {
    return { ...base, lane: "basis_locked",
             reason: "basis is locked by contract — the board cannot move this target" };
  }

  // An HTA locks FUTURES and leaves basis floating; a basis contract is the
  // mirror. Their exposure to a basis move is not this one, so they are named
  // and set aside rather than run through a rule written for cash targets.
  if (base.orderType !== "cash_target") {
    return { ...base, lane: "unsupported", reason: `${base.orderType} does not carry a cash promise this rule can hold` };
  }

  // All three numbers or none — the same rule as the table's
  // cash_target_has_its_numbers constraint, named per field so a null reads as
  // the missing field rather than as a ticket that does not balance.
  for (const [k, label] of [["cashTarget", "cash target"], ["basisAtEntry", "entry basis"],
                            ["futuresTargetEntered", "futures target"]]) {
    if (base[k] == null || !Number.isFinite(base[k])) {
      return { ...base, lane: "ticket_error", reason: `no ${label} on the ticket` };
    }
  }
  // The green ticket prints futures, basis AND cash, so it checks its own
  // arithmetic. A ticket where they disagree is a pen slip, and guessing which
  // of the three is the typo would invent the grower's instruction.
  if (q(base.futuresTargetEntered) + q(base.basisAtEntry) !== q(base.cashTarget)) {
    return {
      ...base,
      lane: "ticket_error",
      reason: `ticket does not balance: ${base.futuresTargetEntered} + ${base.basisAtEntry} != ${base.cashTarget}`,
    };
  }

  const res = resolveCurrentBasis({
    deliveryPoint: base.deliveryPoint,
    commodity: base.commodity,
    deliveryWindow: base.deliveryWindow,
    futuresContract: base.futuresContract,
    bidStructure: base.bidStructure,
    observations,
  });
  if (res.basis === null) {
    return { ...base, lane: "unresolved", reason: res.reason, resolution: res };
  }

  const qDrift = q(res.basis) - q(base.basisAtEntry);          // basis moved this much
  const requiredFutures = unq(q(base.cashTarget) - q(res.basis));
  const adjustment = unq(q(requiredFutures) - q(base.futuresTargetEntered));
  const cashIfFilled = unq(q(base.futuresTargetEntered) + q(res.basis));
  const cashShortfall = unq(q(base.cashTarget) - q(cashIfFilled));
  const bu = Number.isFinite(base.bushels) ? base.bushels : 0;

  return {
    ...base,
    lane: qDrift === 0 ? "unchanged" : qDrift < 0 ? "burn" : "miss",
    reason: null,
    currentBasis: res.basis,
    currentSheetDate: res.currentSheetDate,
    basisSources: res.sources,
    basisDrift: unq(qDrift),
    requiredFutures,
    adjustment,
    cashIfFilled,
    cashShortfall,
    // integer cents x bushels, scaled back once — never `shortfall * bushels`,
    // which is 2800.0000000000005 on Brian Thompson's real ticket.
    dollarsAtStake: unq(q(cashShortfall) * bu),
  };
}

// ── the whole book ─────────────────────────────────────────────────────────
function buildTargetReview(targets, observations) {
  const rows = (targets || []).map((t) => evaluateCashTarget(t, observations));
  const bySize = (a, b) => Math.abs(b.dollarsAtStake || 0) - Math.abs(a.dollarsAtStake || 0);

  const needsChange = rows.filter((r) => r.lane === "burn" || r.lane === "miss").sort(bySize);
  const unchanged = rows.filter((r) => r.lane === "unchanged");
  // Immune by contract, and LISTED rather than dropped — so nobody updates one
  // by hand either. See the basisLocked note in evaluateCashTarget.
  const basisLocked = rows.filter((r) => r.lane === "basis_locked");
  // Every target this cannot answer for is REPORTED, never dropped. A target
  // missing from the list reads as a target that is fine.
  const needsAttention = rows.filter(
    (r) => r.lane === "unresolved" || r.lane === "ticket_error" || r.lane === "unsupported"
  );
  const closed = rows.filter((r) => r.lane === "closed");

  return {
    needsChange,
    unchanged,
    basisLocked,
    needsAttention,
    closed,
    counts: {
      working: rows.length - closed.length,
      burn: needsChange.filter((r) => r.lane === "burn").length,
      miss: needsChange.filter((r) => r.lane === "miss").length,
      unchanged: unchanged.length,
      basisLocked: basisLocked.length,
      needsAttention: needsAttention.length,
    },
    dollarsAtStake: needsChange
      .filter((r) => r.lane === "burn")
      .reduce((s, r) => s + (r.dollarsAtStake || 0), 0),
  };
}

module.exports = {
  sheetStampToDate,
  MAX_BASIS_CENTS,
  parseBasisCents,
  SCALE,
  q,
  unq,
  windowTokens,
  normalizeDeliveryWindow,
  deliveryPointCandidates,
  boardToObservations,
  resolveCurrentBasis,
  LANES,
  evaluateCashTarget,
  buildTargetReview,
};
